<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {computed, ref} from "vue";
import {useStore} from "vuex";
import {filterOutActionFromQueryDataById} from "@/composables/useActions";
import {errorToast, successToast} from "@/composables/useToasts";

defineProps({
  action: {
    type: Object,
    required: true,
  },
});

const queryClient = useQueryClient();

const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);

const showConfirmModal = ref(false);

const optimisticallyMoveAction = async (actionId) => {
  await queryClient.cancelQueries({queryKey: ['actions']});

  return filterOutActionFromQueryDataById(activeTab, queryClient, actionId);
}

const {mutate: deleteAction, isPending} = useMutation({
  mutationFn: async (actionId) => await axios.delete(`/ajax/v2/actions/${actionId}`),
  onMutate: async (actionId) => {
    const previousActions = await optimisticallyMoveAction(actionId);

    successToast('This Manual action has been deleted', 'Deleted');

    return {previousActions}
  },
  onError: (error, _, context) => {
    const {message} = error.response.data;

    errorToast(message ?? 'Something went wrong deleting your action');

    queryClient.setQueryData(['actions'], context.previousActions);
  },
  onSettled: () => {
    showConfirmModal.value = false;
    queryClient.invalidateQueries({ queryKey: ['actions'] });
  }
});
</script>

<template>
  <DefaultButton
    class="tw-text-danger tw-flex tw-items-center tw-gap-2 tw-mr-auto"
    variant="danger"
    size="small"
    @click="showConfirmModal = true"
  >
    <svg-vue
      icon="icons/trash"
      class="tw-w-6"
    />

    <span class="tw-font-bold tw-text-sm">Delete Action</span>
  </DefaultButton>

  <Teleport to=".tailwind">
    <BaseModal
      v-if="showConfirmModal"
      modal-class="tw-max-w-96"
      @close="showConfirmModal = false"
    >
      <div>
        <h2 class="tw-text-2xl tw-text-center tw-font-bold">
          Delete Manual Action?
        </h2>

        <p class="tw-my-6 tw-text-center">
          Are you sure you want to delete this action? This cannot be undone.
        </p>

        <div class="tw-flex tw-justify-center tw-gap-2">
          <DefaultButton @click="showConfirmModal = false">
            Cancel
          </DefaultButton>

          <DefaultButton
            variant="danger"
            :disabled="isPending"
            @click="deleteAction(action.id)"
          >
            <svg-vue
              v-if="isPending"
              class="tw-h-4 tw-mr-2"
              icon="icons/loading"
            />
            {{ isPending ? 'Deleting...' : 'Delete Action' }}
          </DefaultButton>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
